import React from 'react';
import { AppUpdateProvider } from './contexts/app-update-context';
import { AuthProvider } from './contexts/auth/auth-context';
import { OnboardingProvider } from './contexts/onboarding-context';
import { PaymentProvider } from './contexts/payment-context';
import { ReviewBootProvider } from './contexts/review-boot-context';
import { AppBootProvider } from './contexts/app-boot-context';
import { NotificationProvider } from './contexts/notification-context';
import { PromptQueueProvider } from './contexts/prompt-queue-context';
import { ToastProvider, useToast } from './contexts/toast-context';
import { ErrorBoundary } from './components/error-boundary';
import { GlobalNotification } from './components/global-notification/global-notification';
import { PromptQueueOverlay } from './components/permission-prompt-modal/prompt-queue-overlay';
import { NetworkProvider } from './contexts/network-context';
import { NoConnectionScreen } from './components/no-connection-screen/no-connection-screen';
import { PermissionAlertProvider } from './contexts/permission-alert-context';

const ErrorBoundaryWithNotification: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showError } = useToast();

  const handleError = (error: Error) => {
    showError(error.message || 'Something went wrong. Please try again.', 3000);
  };

  return <ErrorBoundary onError={handleError}>{children}</ErrorBoundary>;
};

export const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <NetworkProvider>
      <AppUpdateProvider>
        <AuthProvider>
          <AppBootProvider>
            <PaymentProvider>
              <OnboardingProvider>
                <ReviewBootProvider>
                  <ToastProvider>
                    <NotificationProvider>
                      <PromptQueueProvider>
                        <PermissionAlertProvider>
                          <ErrorBoundaryWithNotification>
                            <GlobalNotification />
                            <PromptQueueOverlay />
                            <NoConnectionScreen />
                            {children}
                          </ErrorBoundaryWithNotification>
                        </PermissionAlertProvider>
                      </PromptQueueProvider>
                    </NotificationProvider>
                  </ToastProvider>
                </ReviewBootProvider>
              </OnboardingProvider>
            </PaymentProvider>
          </AppBootProvider>
        </AuthProvider>
      </AppUpdateProvider>
    </NetworkProvider>
  );
};
